<script setup>
import { computed } from "vue";
import { UserPresenceStatusColor } from "@/shared/config/userPresenceStatuses";

const props = defineProps({
  identifier: { type: String, default: "" },
  seed: { type: String, default: "" },
  name: { type: String, default: "" },
  src: { type: String, default: "" },
  status: { type: String, default: null },
  size: { type: Number, default: 36 },
});

function hashString(str) {
  let h = 2166136261;
  const s = String(str || "");
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

function gradientFor(seed) {
  const hue = hashString(seed) % 360;
  const top = `hsl(${hue}, 55%, 60%)`;
  const bottom = `hsl(${hue}, 60%, 42%)`;
  return `linear-gradient(180deg, ${top} 0%, ${bottom} 100%)`;
}

const initials = computed(() => {
  if (!props.name) return "Н";
  return (
    String(props.name)
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "Н"
  );
});

const effectiveSeed = computed(() => {
  const fromName = String(props.name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .join(" ")
    .toLowerCase();
  return fromName || String(props.identifier || props.seed || "").trim();
});

const gradient = computed(() => gradientFor(effectiveSeed.value));

const boxStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  fontSize:
    props.size <= 28
      ? "11px"
      : props.size <= 36
      ? "12px"
      : props.size <= 48
      ? "14px"
      : "18px",
}));

const statusDot = computed(() => {
  if (!props.status) return null;
  return UserPresenceStatusColor[props.status] || null;
});
</script>

<template>
  <div class="relative inline-flex shrink-0">
    <div
      class="user-avatar"
      :style="[boxStyle, src ? {} : { background: gradient }]"
    >
      <img v-if="src" :src="src" :alt="name" class="user-avatar-img" />
      <span v-else class="user-avatar-initials">{{ initials }}</span>
    </div>
    <span
      v-if="statusDot"
      class="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card"
      :class="statusDot"
    />
  </div>
</template>

<style scoped>
.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  overflow: hidden;
  color: #fff;
  font-weight: 600;
  letter-spacing: 0.02em;
  box-shadow: inset 0 0 0 1px hsl(var(--border) / 0.6);
}

.user-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-avatar-initials {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  font-size: 10px;
}
</style>
